<script setup>
import { ref, computed } from "vue";
import "@/assets/css/mil-pricing.css";
import heroImg from "@/assets/images/icons/hero.png";
import { SAMPLE_CSV } from "../PricingJs/data";
import { parseCSV, exportCSV, fmt$, fmtp } from "../PricingJs/engine";
import ComplianceCard from "../MILComo/ComplianceCard.vue";
import HeaderComponent from "@/components/commoncomponents/HeaderComponent.vue";

const props = defineProps(["items", "lots", "stage", "insights", "headers"]);
const emit = defineEmits(["reset", "run"]);

const totalRecovery = computed(() => props.lots.reduce((s, l) => s + l.suggestedPrice, 0));
const totalInventory = computed(() => (props.items || []).reduce((s, i) => s + (i.quantity || 1), 0));
const fileRef = ref(null);
const csvText = ref("");
const focusLot = ref(null);
const currentPage = ref(1);
const itemsPerPage = 10;
const headerText = ref('Lot Pricing Optimizer');

import { watch } from "vue";
watch(() => props.lots, () => { currentPage.value = 1; }, { deep: false });

const totalPages = computed(() => Math.ceil((props.lots || []).length / itemsPerPage));
const paginatedLots = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  return (props.lots || []).slice(start, start + itemsPerPage);
});

const loadFile = (file) => {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => (csvText.value = e.target.result || "");
  reader.readAsText(file);
};

const handleRun = () => {
  if (csvText.value) emit("run", parseCSV(csvText.value));
};

const useSample = () => {
  emit("run", parseCSV(SAMPLE_CSV));
};

const stats = [
  { label: "Total Inventory", val: "12,456", sub: "Total Items", color: "#0067ff", icon: "📋" },
  { label: "Est. Recovery Increase", val: "18.6%", sub: "vs Traditional Pricing", color: "#00d09c", icon: "💹" },
  { label: "Active Lots", val: "1,234", sub: "Generated", color: "#8b5cf6", icon: "📦" },
  { label: "Last Updated", val: "24 Apr 2025", sub: "10:30 AM", color: "#f59e0b", icon: "📅" },
];

const kpis = computed(() => [
  { label: "Total Inventory", sub: "Units Analyzed", val: totalInventory.value.toLocaleString("en-IN"), icon: "📋", bg: "#eff6ff", text: "#0067ff" },
  { label: "Total Recoverable", sub: "Calculated Net", val: fmt$(totalRecovery.value), icon: "₹", bg: "#f0fdf4", text: "#00d09c" },
  { label: "Lot Distributions", sub: "Optimized Units", val: (props.lots || []).length.toLocaleString("en-IN"), icon: "📦", bg: "#f5f3ff", text: "#8b5cf6" },
]);
</script>

<template>
  <header-component :headerTitle="headerText" />
  <div v-if="!lots || lots.length === 0" style="padding: 40px; flex: 1; display: flex; flex-direction: column; gap: 24px; animation: fadeIn .4s ease">
    <div class="card" style="display: flex; align-items: center; justify-content: space-between; padding: 32px 48px; overflow: hidden">
      <div style="flex: 1">
        <h1 style="font-size: 48px; font-weight: 800; color: #1e293b; margin: 0; letter-spacing: -0.04em">
          Lot Pricing <span style="color: #0067ff">Optimizer</span>
        </h1>
        <p style="margin-top: 16px; color: #64748b; font-size: 17px; max-width: 540px; line-height: 1.6">
          Transform inventory into optimized lots with Blubirch MIL engine. Maximize recovery with AI-driven pricing.
        </p>
      </div>
      <div style="width: 340px; height: 200px; position: relative">
        <img :src="heroImg" alt="Hero" style="width: 100%; height: 100%; object-fit: contain" />
        <div style="position: absolute; top: 10px; right: 10px; width: 44px; height: 44px; border-radius: 50%; background: #0067ff; color: white; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 13px; box-shadow: 0 8px 16px rgba(0,103,255,0.3)">AI</div>
      </div>
    </div>
    <div class="card" style="padding: 40px; text-align: center">
      <div class="upload-zone" @click="fileRef.click()">
        <input ref="fileRef" type="file" style="display: none" @change="(e) => loadFile(e.target.files[0])" />
        <div style="font-size: 48px; color: #0067ff; margin-bottom: 20px">📄</div>
        <div style="font-weight: 800; font-size: 22px; color: #1e293b">{{ csvText ? "CSV Ready to Process" : "Drop your inventory CSV here" }}</div>
        <div style="font-size: 14px; color: #94a3b8; margin-top: 8px">Support formats: .csv, .xlsx, .txt</div>
      </div>
      <div style="display: flex; gap: 16px; margin-top: 24px">
        <button class="btn btn-outline" style="flex: 1; padding: 18px; border-radius: 16px" @click="useSample"><span>📋</span> Use Demo Data</button>
        <button class="btn btn-primary" style="flex: 1.5; padding: 18px; border-radius: 16px" :disabled="!csvText" @click="handleRun">Generate Pricing Mode <span>→</span></button>
      </div>
    </div>
    <ComplianceCard :dark="true" />
    <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px">
      <div v-for="s in stats" :key="s.label" class="card" style="margin: 0; padding: 24px; display: flex; align-items: center; gap: 16px">
         <div :style="{ width: '48px', height: '48px', background: `${s.color}15`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: s.color }">{{ s.icon }}</div>
         <div>
           <div style="font-size: 12px; font-weight: 700; color: #94a3b8; margin-bottom: 2px">{{ s.label }}</div>
           <div style="font-size: 20px; font-weight: 800; color: #1e293b">{{ s.val }}</div>
           <div style="font-size: 11px; color: #64748b; margin-top: 2px">{{ s.sub }}</div>
         </div>
      </div>
    </div>
  </div>

  <div v-else style="flex: 1; display: flex; flex-direction: column; gap: 24px; animation: fadeUp .6s both">
    <div class="card-container">
  <div class="card-content">
    
    <!-- LEFT SECTION -->
    <div class="text-section">
      <h1 class="title">
        Lot Pricing <span class="highlight">Optimizer</span>
      </h1>

      <p class="subtitle">
        Success! The MIL engine has analyzed {{ totalInventory }} units and 
        synthesized {{ lots.length }} optimized lots for maximum recovery.
      </p>

      <button class="primary-btn" @click="emit('reset')">
        New Portfolio Analysis
      </button>
    </div>

    <!-- RIGHT IMAGE -->
    <div class="image-section">
      <img :src="heroImg" alt="Hero" />
    </div>

  </div>
</div>
    <div style="display: grid; grid-template-columns: 1fr 360px; gap: 24px; align-items: start">
      <div style="display: flex; flex-direction: column; gap: 24px">
        <div class="card" style="margin: 0; padding: 32px !important; background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%); position: relative; overflow: hidden">
           <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px">
             <div style="font-weight: 800; font-size: 20px; color: #1e293b">Calculated Portfolio Summary</div>
             <button class="btn btn-outline" style="font-size: 12px !important; padding: 8px 16px !important" @click="exportCSV(lots, headers)">Download CSV Report</button>
           </div>
           <div style="border: 1px solid #f1f5f9; border-radius: 16px; overflow: hidden">
              <table style="width: 100%; border-collapse: collapse; font-size: 14px">
                 <thead>
                    <tr style="background: #f8fafc; text-align: left">
                       <th style="padding: 16px; color: #64748b; font-weight: 700;">LOT ID</th>
                       <th style="padding: 16px; color: #64748b; font-weight: 700">UNITS</th>
                       <th style="padding: 16px; color: #64748b; font-weight: 700">EST. RECOVERY</th>
                       <th style="padding: 16px; color: #64748b; font-weight: 700">YIELD (%)</th>
                       <th style="padding: 16px; color: #64748b; font-weight: 700; text-align: right">ACTION</th>
                    </tr>
                 </thead>
                 <tbody>
                    <tr v-for="lot in paginatedLots" :key="lot.lot_id" style="border-bottom: 1px solid #f1f5f9">
                       <td style="padding: 16px; font-weight: 800; color: #1e293b; text-align: left">{{ lot.lot_id }}</td>
                       <td style="padding: 16px; color: #475569; text-align: left">{{ lot.n }} Units</td>
                       <td style="padding: 16px; font-weight: 700; color: #00d09c; text-align: left">{{ fmt$(lot.suggestedPrice) }}</td>
                       <td style="padding: 16px; font-weight: 700; color: #0067ff; text-align: left">{{ fmtp(lot.realisationRate) }}</td>
                       <td style="padding: 16px; text-align: right">
                         <button class="btn btn-outline" style="padding: 6px 12px; font-size: 11px" @click="focusLot = lot">View Details</button>
                       </td>
                    </tr>
                 </tbody>
              </table>
           </div>

           <!-- Pagination Controls -->
           <div v-if="totalPages > 1" style="display: flex; justify-content: space-between; align-items: center; margin-top: 16px; padding: 0 8px">
              <div style="font-size: 13px; color: #64748b">
                Showing {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, lots.length) }} of {{ lots.length }} Lots
              </div>
              <div style="display: flex; gap: 8px">
                <button class="btn btn-outline" style="padding: 6px 12px; font-size: 12px" :disabled="currentPage === 1" @click="currentPage--">Previous</button>
                <div style="display: flex; align-items: center; gap: 4px; font-size: 13px; font-weight: 700; color: #1e293b; padding: 0 12px">
                  Page {{ currentPage }} of {{ totalPages }}
                </div>
                <button class="btn btn-outline" style="padding: 6px 12px; font-size: 12px" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
              </div>
           </div>
        </div>
        
        <div class="card" style="margin: 0; padding: 32px; background: linear-gradient(135deg, #ffffff 0%, #f8fbff 100%); position: relative; overflow: hidden">
           <div style="position: absolute; top: -20px; right: -20px; width: 140px; height: 140px; background: rgba(0,103,255,0.03); border-radius: 50%" />
           <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px">
              <div style="width: 32px; height: 32px; background: #0067ff; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 16px">✨</div>
              <h3 style="font-size: 20px; font-weight: 800; color: #1e293b; margin: 0">AI Perspectives</h3>
           </div>
           <div v-if="insights" style="font-size: 15px; color: #475569; line-height: 1.8; white-space: pre-wrap">
             {{ insights }}
           </div>
           <div v-else style="display: flex; flex-direction: column; gap: 12px">
             <div style="height: 16px; width: 100%; background: #f1f5f9; border-radius: 4px; animation: pulse 1.5s infinite" />
             <div style="height: 16px; width: 90%; background: #f1f5f9; border-radius: 4px; animation: pulse 1.5s infinite" />
             <div style="height: 16px; width: 95%; background: #f1f5f9; border-radius: 4px; animation: pulse 1.5s infinite" />
             <p style="margin: 16px 0 0; font-size: 12px; color: #94a3b8; font-weight: 700; letter-spacing: 0.05em">SYNTHESIZING MARKET INTELLIGENCE...</p>
           </div>
        </div>
      </div>
      
      <div style="display: flex; flex-direction: column; gap: 16px">
         <div v-for="k in kpis" :key="k.label" class="kpi-card" style="margin: 0; padding: 32px">
            <div class="kpi-icon" :style="{ background: k.bg, color: k.text, width: '64px', height: '64px', fontSize: '28px' }">{{ k.icon }}</div>
            <div>
              <div style="font-size: 12px; color: #94a3b8; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em">{{ k.label }}</div>
              <div style="font-size: 28px; font-weight: 800; color: #1e293b; margin: 4px 0">{{ k.val }}</div>
              <div style="font-size: 12px; color: #64748b">{{ k.sub }}</div>
            </div>
         </div>
      </div>
    </div>

    <!-- Lot Details Modal -->
    <div v-if="focusLot" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,17,75,0.4); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 40px">
       <div class="card" style="width: 100%; max-width: 1000px; max-height: 80vh; overflow: hidden; display: flex; flex-direction: column; margin: 0; box-shadow: 0 50px 100px rgba(0,0,0,0.3)">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px">
             <div>
                <h2 style="font-size: 32px; font-weight: 800; color: #1e293b; margin: 0; text-align: left">{{ focusLot.lot_id }} Details</h2>
                <p style="color: #64748b; margin: 8px 0 0">Comprehensive asset breakdown for optimized portfolio selection.</p>
             </div>
             <button @click="focusLot = null" style="width: 44px; height: 44px; border-radius: 50%; border: 1px solid #e2e8f0; background: white; font-size: 20px; cursor: pointer; display: flex; align-items: center; justify-content: center">✕</button>
          </div>
          
          <div style="flex: 1; overflow-y: auto; border: 1px solid #f1f5f9; border-radius: 20px">
             <table style="width: 100%; border-collapse: collapse; font-size: 13px">
                <thead>
                   <tr style="background: #f8fafc; text-align: left; position: sticky; top: 0">
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">#</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">Tag Number</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">CATEGORY</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">BRAND</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">MODEL</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">GRADE</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700">Quntity</th>
                      <th style="padding: 16px; color: #94a3b8; font-weight: 700; text-align: right">RETAIL PRICE</th>
                   </tr>
                </thead>
                <tbody>
                   <tr v-for="(it, idx) in focusLot.items" :key="it.item_id" style="border-bottom: 1px solid #f1f5f9">
                      <td style="padding: 14px 16px; color: #94a3b8; text-align: left">{{ idx + 1 }}</td>
                      <td style="padding: 14px 16px; color: #64748b; text-align: left">{{ it.item_id || "—" }}</td>
                      <td style="padding: 14px 16px; font-weight: 700; color: #1e293b; text-align: left">{{ it.category }}</td>
                      <td style="padding: 14px 16px; color: #475569; text-align: left">{{ it.brand }}</td>
                      <td style="padding: 14px 16px; color: #64748b; text-align: left">{{ it.model || "—" }}</td>
                      <td style="padding: 14px 16px; text-align: left"><span class="badge" style="background: #f1f5f9; color: #1e293b">{{ it.grade }}</span></td>
                      <td style="padding: 14px 16px; color: #475569; text-align: left">{{ it.quantity || 1 }}</td>
                      <td style="padding: 14px 16px; text-align: right; font-weight: 700; color: #1e293b; text-align: right">{{ fmt$(it.pricing_base || it.retail_price) }}</td>
                   </tr>
                </tbody>
             </table>
          </div>
          
          <div style="display: flex; justify-content: space-between; margin-top: 32px; padding: 24px 32px; background: #f8fbff; border-radius: 20px; border: 1px solid #e2efff">
             <div style="display: flex; gap: 32px">
               <div>
                 <div style="font-size: 11px; color: #94a3b8; font-weight: 700">TOTAL ITEMS</div>
                 <div style="font-size: 20px; font-weight: 800; color: #1e293b">{{ focusLot.n }} Units</div>
               </div>
               <div>
                 <div style="font-size: 11px; color: #94a3b8; font-weight: 700">TOTAL RETAIL</div>
                 <div style="font-size: 20px; font-weight: 800; color: #0067ff">{{ fmt$(focusLot.totalRetail) }}</div>
               </div>
             </div>
             <div style="text-align: right">
               <div style="font-size: 11px; color: #94a3b8; font-weight: 700">EST. RECOVERY</div>
               <div style="font-size: 24px; font-weight: 800; color: #00d09c">{{ fmt$(focusLot.suggestedPrice) }}</div>
             </div>
          </div>
       </div>
    </div>
  </div>
</template>
<style scoped>
.card-container {
  background: #f8fafc;
  border-radius: 16px;
  padding: 40px 60px;
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.text-section {
  flex: 1;
  text-align: left;
}

.title {
  font-size: 48px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
}

.highlight {
  color: #3b82f6;
}

.subtitle {
  margin-top: 20px;
  color: #64748b;
  font-size: 16px;
  max-width: 520px;
  line-height: 1.6;
}

.primary-btn {
  margin-top: 24px;
  background-color: #2563eb;
  color: white;
  border: none;
  padding: 12px 22px;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
}

.primary-btn:hover {
  background-color: #1d4ed8;
}

.image-section {
  width: 360px;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-section img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
